"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldVerticalSeparation = exports.ModelMutatorVerticalSeparation = void 0;
const core_api_1 = require("@ikegps/core-api");
const unit_1 = require("../../../unit");
const collection_field_1 = require("../collection.field");
const marker_drawable_1 = require("../drawable/marker.drawable");
const vertical_separation_drawable_1 = require("../drawable/vertical.separation.drawable");
class ModelMutatorVerticalSeparation extends collection_field_1.ModelMutator {
    manual(newValue) {
        this.unlink();
        if (newValue == null || isNaN(newValue)) {
            this.state.setFieldValue(this.field.id, null);
        }
        else {
            this.state.setFieldValue(this.field.id, {
                value: newValue,
            });
        }
    }
    clear() {
        this.manual(null);
    }
    drawable(marker) {
        var _a;
        // If this is the first marker to be linked,
        // or an existing top marker link is in a different Capture,
        // then set a new top marker with no bottom marker.
        if (!((_a = this.field.stateValue) === null || _a === void 0 ? void 0 : _a.drawableTop) || marker.captureId !== this.field.stateValue.captureId) {
            this.state.setFieldValue(this.field.id, {
                drawableTop: marker.id,
                drawableBottom: '',
                captureId: marker.captureId,
            });
            return;
        }
        // If the top has already been linked then set the bottom marker
        // and create/update the Vertical Separation Drawable.
        const drawable = this.field.drawable
            ? this.field.drawable
            : vertical_separation_drawable_1.CollectionModelDrawableVerticalSeparation.createWithLabel(this.field.collection.getDrawableState(marker.captureId), this.field.stateValue.drawableTop, marker.id, this.field.id);
        this.state.setFieldValue(this.field.id, {
            drawableBottom: marker.id,
            drawableTop: this.field.stateValue.drawableTop,
            captureId: marker.captureId,
            drawableVerticalSeparation: drawable.id,
        });
    }
    // This method is used to set the top / bottom drawables and label states
    // directly, during deserialization.
    setDrawables(drawableTop, drawableBottom, sep) {
        this.state.setFieldValue(this.field.id, {
            drawableTop: drawableTop.id,
            drawableBottom: drawableBottom === null || drawableBottom === void 0 ? void 0 : drawableBottom.id,
            captureId: drawableTop.captureId,
            drawableVerticalSeparation: sep === null || sep === void 0 ? void 0 : sep.id,
        });
    }
    unlink() {
        var _a, _b, _c, _d;
        // Only unlink if currently linked.
        if (!((_a = this.field.stateValue) === null || _a === void 0 ? void 0 : _a.drawableTop) &&
            !((_b = this.field.stateValue) === null || _b === void 0 ? void 0 : _b.drawableBottom) &&
            !((_c = this.field.stateValue) === null || _c === void 0 ? void 0 : _c.drawableVerticalSeparation)) {
            return;
        }
        const stateValue = this.field.stateValue;
        if (stateValue == null || stateValue.drawableTop == null || stateValue.captureId == null) {
            // Partial state — remove VS drawable if accessible, but nothing to unlink
            (_d = this.field.drawable) === null || _d === void 0 ? void 0 : _d.remove();
            return;
        }
        this.state.setFieldValue(this.field.id, null);
        const drawableState = this.field.collection.getDrawableState(stateValue.captureId);
        // Unlink this field from the top and bottom markers
        if (drawableState.hasDrawable(stateValue.drawableTop)) {
            const drawableTop = drawableState.getDrawable(stateValue.drawableTop);
            drawableTop.label.unlink(this.field.id);
        }
        if (stateValue.drawableBottom && drawableState.hasDrawable(stateValue.drawableBottom)) {
            const drawableBottom = drawableState.getDrawable(stateValue.drawableBottom);
            drawableBottom.label.unlink(this.field.id);
        }
        // Only remove the VS drawable if no other field still references it.
        // During same-collection copy (duplication), the VS drawable is shared between
        // the original and duplicate. Removing it here would destroy the original's measurement.
        if (stateValue.drawableVerticalSeparation && drawableState.hasDrawable(stateValue.drawableVerticalSeparation)) {
            const isStillReferenced = drawableState.hasDrawable(stateValue.drawableTop) &&
                drawableState
                    .getDrawable(stateValue.drawableTop)
                    .label.links.some((link) => {
                    var _a;
                    return CollectionModelFieldVerticalSeparation.isVerticalSeparationField(link) &&
                        ((_a = link.stateValue) === null || _a === void 0 ? void 0 : _a.drawableVerticalSeparation) === stateValue.drawableVerticalSeparation;
                });
            if (!isStillReferenced) {
                drawableState
                    .getDrawable(stateValue.drawableVerticalSeparation)
                    .remove();
            }
        }
    }
    linkExistingMarkerOrCopy(destinationDrawableState, originalDrawable) {
        let myCopiedDrawable = null;
        // if original drawable is linked to multiple fields, check if already copied
        if (originalDrawable.label.links.length > 1) {
            const existingCopiedDrawable = destinationDrawableState
                .getAllDrawables()
                .filter((copiedDrawable) => copiedDrawable instanceof marker_drawable_1.CollectionModelDrawableMarker && copiedDrawable.y === originalDrawable.y)[0];
            myCopiedDrawable = existingCopiedDrawable ? existingCopiedDrawable : null;
        }
        // create new drawable if not already existing
        if (!myCopiedDrawable) {
            myCopiedDrawable = originalDrawable.clone(destinationDrawableState);
        }
        myCopiedDrawable.label.linkVerticalSeparation(this.field);
        return myCopiedDrawable;
    }
    copy(from) {
        if (!from.stateValue) {
            this.clear();
            return true;
        }
        // Clean up existing drawables before copying new ones.
        // Without this, a re-link leaves orphaned VS drawables in the drawable state
        // which causes errors when sorting by field value (stale _fieldId references).
        this.unlink();
        const fromCollection = from.collection;
        const toCollection = this.field.collection;
        const captureId = from.stateValue.captureId;
        // Do not copy if missing capture or if source field is not fully linked to top and bottom markers
        if (!captureId ||
            !from.stateValue.drawableTop ||
            !from.stateValue.drawableBottom ||
            !from.stateValue.drawableVerticalSeparation) {
            return false;
        }
        const fromDrawableState = fromCollection.getDrawableState(captureId);
        const drawableTop = fromDrawableState.getDrawable(from.stateValue.drawableTop);
        const drawableBottom = fromDrawableState.getDrawable(from.stateValue.drawableBottom);
        const drawable = fromDrawableState.getDrawable(from.stateValue.drawableVerticalSeparation);
        // If copying to another collection, try to link to existing markers at the same heights before creating new ones.
        if (fromCollection !== toCollection) {
            const toDrawableState = toCollection.getDrawableState(captureId);
            if (toDrawableState.base == null) {
                this.copyBaseMarkers(fromCollection, captureId);
            }
            const copyDrawableTop = this.linkExistingMarkerOrCopy(toDrawableState, drawableTop);
            const copyDrawableBottom = this.linkExistingMarkerOrCopy(toDrawableState, drawableBottom);
            const copyDrawable = drawable.clone(toDrawableState);
            this.setDrawables(copyDrawableTop, copyDrawableBottom, copyDrawable);
            return true;
        }
        // Otherwise just reuse the existing markers by adding a new link to the existing markers to the field being copied into.
        this.setDrawables(drawableTop, drawableBottom, drawable);
        drawableTop.label.linkVerticalSeparation(this.field);
        drawableBottom.label.linkVerticalSeparation(this.field);
        return true;
    }
    // duplicated in height.field.ts
    copyBaseMarkers(fromCollection, captureId) {
        const collection = this.field.collection;
        const drawableState = collection.getDrawableState(captureId);
        const fromDrawableState = fromCollection.getDrawableState(captureId);
        const fromBaseMarker = fromDrawableState.base;
        if (fromBaseMarker != null) {
            const { marker: baseMarker } = marker_drawable_1.CollectionModelDrawableMarker.createWithLabel(drawableState, fromBaseMarker.getVector(), fromBaseMarker.label.getVector(), undefined, core_api_1.MarkerType.BASE);
            const fromBaseOffsetMarker = fromDrawableState.baseOffset;
            if (fromBaseOffsetMarker != null) {
                baseMarker.offset = fromBaseMarker.offset;
            }
        }
    }
}
exports.ModelMutatorVerticalSeparation = ModelMutatorVerticalSeparation;
class CollectionModelFieldVerticalSeparation extends collection_field_1.CollectionModelField {
    constructor() {
        super(...arguments);
        this._Mutator = new ModelMutatorVerticalSeparation(this);
    }
    static isVerticalSeparationField(f) {
        return f.schema.type === CollectionModelFieldVerticalSeparation.TYPE;
    }
    get Mutator() {
        return this._Mutator;
    }
    hasValue() {
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return false;
        }
        if (!stateValue.drawableTop || !stateValue.drawableBottom) {
            return false;
        }
        return true;
    }
    hasDrawableTop() {
        const stateValue = this.stateValue;
        if (stateValue == null || !stateValue.drawableTop) {
            return false;
        }
        return true;
    }
    hasDrawableBottom() {
        const stateValue = this.stateValue;
        if (stateValue == null || !stateValue.drawableBottom) {
            return false;
        }
        return true;
    }
    get value() {
        var _a, _b;
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return null;
        }
        if (((_a = this.drawableTop) === null || _a === void 0 ? void 0 : _a.value) == null || ((_b = this.drawableBottom) === null || _b === void 0 ? void 0 : _b.value) == null) {
            return null;
        }
        return this.drawableTop.value - this.drawableBottom.value;
    }
    get drawable() {
        var _a, _b;
        if (!((_a = this.stateValue) === null || _a === void 0 ? void 0 : _a.captureId) || !((_b = this.stateValue) === null || _b === void 0 ? void 0 : _b.drawableVerticalSeparation)) {
            return null;
        }
        const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
        return drawableState.getDrawable(this.stateValue.drawableVerticalSeparation);
    }
    get drawableTop() {
        var _a, _b;
        if (!((_a = this.stateValue) === null || _a === void 0 ? void 0 : _a.captureId) || !((_b = this.stateValue) === null || _b === void 0 ? void 0 : _b.drawableTop)) {
            return null;
        }
        const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
        return drawableState.getDrawable(this.stateValue.drawableTop);
    }
    get drawableBottom() {
        var _a, _b, _c;
        if (!((_a = this.stateValue) === null || _a === void 0 ? void 0 : _a.captureId) || !((_b = this.stateValue) === null || _b === void 0 ? void 0 : _b.drawableTop) || !((_c = this.stateValue) === null || _c === void 0 ? void 0 : _c.drawableBottom)) {
            return null;
        }
        const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
        const d = drawableState.getDrawable(this.stateValue.drawableBottom);
        const top = drawableState.getDrawable(this.stateValue.drawableTop);
        top.pair = addPair(updatePair(top.pair), d.id);
        d.pair = addPair(updatePair(d.pair), top.id);
        return d;
    }
    get clearanceThresholdInMeters() {
        var _a;
        return (_a = this.schema.clearanceThresholdInMeters) !== null && _a !== void 0 ? _a : null;
    }
    get annotationLabel() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        //if we're self-title no need to search for field just return displayname
        const fieldId = this.schema.annotationLabelFieldId;
        if (fieldId === this.schema.id) {
            return this.schema.displayName;
        }
        const field = fieldId ? this.findAncestorField(this.parent, fieldId) : null;
        if (!field || !field.schema.type) {
            return (_a = this.parent.title) !== null && _a !== void 0 ? _a : '';
        }
        switch (field.schema.type) {
            case core_api_1.IkeFieldType.ANCHOR:
                // I think to get the complex values we have to type them first and put them together ourselves
                if (field.hasValue()) {
                    let anchor = field.value;
                    return `${anchor.collectionTool} ${anchor.placement}`;
                }
                return field.schema.displayName;
            case core_api_1.IkeFieldType.DATE:
                let date = field;
                if (!date.hasValue() || date.displayValue == null || date.displayValue === '') {
                    return (_b = this.parent.title) !== null && _b !== void 0 ? _b : '';
                }
                return date.displayValue;
            case core_api_1.IkeFieldType.FILE_TOOL:
                let fileTool = field;
                if (!fileTool.hasValue() || fileTool.displayValue == null || fileTool.displayValue === '') {
                    return (_c = this.parent.title) !== null && _c !== void 0 ? _c : '';
                }
                return fileTool.displayValue;
            case core_api_1.IkeFieldType.FORM:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.HEIGHT:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.IMAGE:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.LOCATION:
                let loc = field;
                if (!loc.hasValue() || loc.displayValue == null || loc.displayValue === '') {
                    return (_d = this.parent.title) !== null && _d !== void 0 ? _d : '';
                }
                let locVal = loc.value;
                return `${locVal.latitude.toFixed(2)},${locVal.longitude.toFixed(2)}`;
            case core_api_1.IkeFieldType.NESTED_LIST:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return (_e = this.parent.title) !== null && _e !== void 0 ? _e : '';
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.NUMBER:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return (_f = this.parent.title) !== null && _f !== void 0 ? _f : '';
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.SELECT_LIST:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return (_g = this.parent.title) !== null && _g !== void 0 ? _g : '';
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.TEXT:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return (_h = this.parent.title) !== null && _h !== void 0 ? _h : '';
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.TRUE_SIZE:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.VECTOR:
                let vector = field.value;
                if (field.hasValue()) {
                    return `${unit_1.IkeUnit.display(vector.distance, this.distanceUnit, unit_1.IkeUnit.METER, core_api_1.IkeUnitType.DISTANCE, 1)} ${unit_1.IkeUnit.display(vector.bearing, unit_1.IkeUnit.DEGREE, unit_1.IkeUnit.RADIAN, core_api_1.IkeUnitType.ANGLE, 1)}`;
                }
                return field.schema.displayName;
            case core_api_1.IkeFieldType.VERTICAL_SEPARATION:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.YES_NO:
                return field.schema.displayName;
            default:
                return (_j = this.parent.title) !== null && _j !== void 0 ? _j : '';
        }
    }
    getOwnedCaptures() {
        return [];
    }
    getCaptures() {
        return this.getCaptureIds()
            .map((c) => this.collection.state.getCapture(c))
            .filter((c) => !!c);
    }
    getOwnedCaptureIds() {
        return this.getCaptureIds();
    }
    getCaptureIds() {
        return [];
    }
}
exports.CollectionModelFieldVerticalSeparation = CollectionModelFieldVerticalSeparation;
CollectionModelFieldVerticalSeparation.TYPE = core_api_1.IkeFieldType.VERTICAL_SEPARATION;
const updatePair = (pair) => {
    if (typeof pair === 'string') {
        return [pair];
    }
    return pair;
};
const addPair = (pair, newPairedId) => {
    if (pair == null) {
        return [newPairedId];
    }
    if (pair.includes(newPairedId)) {
        return pair;
    }
    return [...pair, newPairedId];
};
//# sourceMappingURL=vertical.separation.field.js.map